import Handlebars from 'handlebars'
import {createBrowserHistory} from 'history'

import {isDefaultPrevented} from '@base/dom-utils/events'
import openTab from '@base/dom-utils/openTab'
import {isBaseHost} from '@base/dom-utils/urls/isBaseHost'
import sanitizeUrl, {
  hasProperScheme,
  isHttpUrl,
} from '@base/url-utils/sanitizeUrl'

import type {SafeString} from '@base-uikit/feedback-logic/types'
import {
  refreshOnNavigationScheduled as scheduleForceRefreshAction,
  showNotFoundPage as showNotFoundPageAction,
  showErrorPage as showErrorPageAction,
} from '@base-uikit/navigation/actions'
import {refreshOnNavigationSelector} from '@base-uikit/navigation/selectors'

import {captureError} from './prober'
import getStore from './store'

let historyInstance

const blockedListeners = new Map<string, Function>()
let blockedKeys: string[] = []
let lastBlockedURL: undefined | string | LocationType | SafeString = undefined

export type LocationType = {
  pathname: string
  search?: string | null
  hash?: string | null
  state?: any | null
}

export function overrideHistoryInstance(instance?: any | null) {
  historyInstance = instance
}

export function history(): {
  push: Function
  replace: Function
  goBack: Function
  location: any
  listen: Function
} {
  if (!historyInstance) {
    historyInstance = createBrowserHistory()
  }
  return historyInstance
}

/**
 * Block navigation and execute a function instead. Multiple blocks can be stacked,
 * each one identified by a unique given key.
 *
 * IMPORTANT: When adding a new blocking listener, it has to be removed manually
 * with unblockNavigation
 * @param key Identifier for the listener
 * @param listener Function to run when trying to change the url through a push
 */
export const blockNavigation = (key: string, listener: Function): void => {
  blockedListeners.set(key, listener)
  if (!blockedKeys.includes(key)) {
    blockedKeys.push(key)
  }
}

export function navigateTo(
  /* location can be either of those types as per react-router docs:
  push(path, [state]) - (function) Pushes a new entry onto the history stack
  where path is of type string | Partial<Path>
  https://github.com/remix-run/history/blob/dev/packages/history/index.ts */
  location: string | LocationType | SafeString,
  state?: any
): void {
  if (blockedListeners.size === 0) {
    history().push(
      location instanceof Handlebars.SafeString
        ? location.toString()
        : location,
      state
    )
  } else {
    // There is, at least, one listener blocking the navigation
    // Store this URL to push it in case of unblocking
    lastBlockedURL = location
    // Get the last registered key and run its listener
    const key = blockedKeys[blockedKeys.length - 1]
    const listener = blockedListeners.get(key)
    if (listener) {
      listener()
    }
  }
}

/**
 * Unblocks the navigation removing the listener identified by its key and navigating to the last blocked URL.
 * @param key Identifier of the listener to be removed
 * @param shouldNavigate Indicates of, after unblocking the navigation, we should navigate to lastBlockedURL (false by default)
 */
export const unblockNavigation = (
  key: string,
  shouldNavigate = false
): void => {
  if (blockedListeners.has(key)) {
    blockedListeners.delete(key)
    blockedKeys = blockedKeys.filter((k) => k !== key)
  }
  if (shouldNavigate && lastBlockedURL) {
    navigateTo(lastBlockedURL)
  }
  lastBlockedURL = undefined
}

export function replaceLocation(
  location: string | LocationType | SafeString,
  state?: any
): void {
  history().replace(
    location instanceof Handlebars.SafeString ? location.toString() : location,
    state
  )
}

type BeforeRefreshFn = () => Promise<any> | void
const beforeRefreshCallbacks: Array<BeforeRefreshFn> = []

// Register an asynchronous function to be run before page is refreshed
export function runBeforeRefresh(fn: BeforeRefreshFn) {
  beforeRefreshCallbacks.push(fn)
}

async function onBeforeRefresh() {
  const callbacks = Promise.all(
    beforeRefreshCallbacks.map(async (fn) => {
      try {
        await fn()
      } catch (e) {
        // we don't want errors in one callback to affect all the others
        captureError('onBeforeRefresh', e)
      }
    })
  )
  // wait no more than 2 seconds before navigating away
  // this should be enough to at least push data to our backends (we don't need the result)
  const timeout = new Promise((resolve) => setTimeout(resolve, 2000))
  await Promise.race([callbacks, timeout])
}

// Reloads the page
export async function refresh() {
  await onBeforeRefresh()
  window.location.reload()
}

// Reloads the page to specified location
export async function refreshTo(location: string) {
  await onBeforeRefresh()
  // @ts-ignore
  window.location = location
}

export function getPathname(): string {
  return history().location.pathname
}

// Helper function that indicates weather or not old app's route handler should be called
// This is an abstraction over trigger option in Backbone.history.navigate
export function shouldTriggerRouteHandler(): boolean {
  const {location} = history()
  if (!location.state) {
    return true
  } else if (
    location.state.trigger !== undefined &&
    location.state.trigger === false
  ) {
    return false
  }
  return true
}

export function scheduleRefreshOnNextNavigation(reason: string) {
  getStore().dispatch(scheduleForceRefreshAction(reason || 'unknown'))
}

export function isRefreshOnNavigationScheduled(): boolean {
  return !!refreshOnNavigationSelector(getStore().getState())
}

export function handleNavigationClick(
  _url: string,
  _target?: string | null,
  event?: any | null
) {
  if (event && event.altKey) {
    return
  }

  if (event && isDefaultPrevented(event)) {
    return
  }

  // Make sure we have a string here, sometimes we may get a SafeString for example
  const url = String(_url || '')
  const isAbsoluteUrl = isHttpUrl(url)
  const isUnsupportedUrl = url && sanitizeUrl(url) !== url.trim()
  // all urls with schema (javascript:, data:), but not http:
  const isSpecialUrl = !isAbsoluteUrl && hasProperScheme(url)
  const isExternalUrl = isAbsoluteUrl && !isBaseHost(url)
  const isHashUrl = url.startsWith('#')
  const forceRefereshScheduled = isRefreshOnNavigationScheduled()

  const target =
    event && (event.metaKey || event.ctrlKey) ? '_blank' : _target || '_self'

  const element = event && event.currentTarget

  // Only <A> elements with equal href that are mouse-clicked will navigate on browsers
  const safeForBrowserHandling =
    element instanceof HTMLAnchorElement &&
    event &&
    event.type === 'click' &&
    element.getAttribute('href') === url

  if (isSpecialUrl || isUnsupportedUrl) {
    if (event && (isUnsupportedUrl || target !== '_self')) {
      event.preventDefault()
    }
    // Let browser handle it
    return
  }

  if (
    target === '_self' &&
    !isAbsoluteUrl &&
    !isHashUrl &&
    !forceRefereshScheduled
  ) {
    // navigate internally
    if (event) event.preventDefault()
    navigateTo(url)
  } else if (!safeForBrowserHandling || isExternalUrl) {
    // navigate using window.open for non-anchors and external links
    if (event) event.preventDefault()
    openTab(url, target)
  } else if (forceRefereshScheduled && !isHashUrl) {
    if (event) event.preventDefault()
    refreshTo(url)
  }
  // Otherwise, let browser handle the click
}

// Temporary solution
// to be removed when handleNavigationClick waits for Prober data
export async function handleNavigationClickWaitForProber(
  _url: string,
  _target?: string | null,
  event?: any | null
) {
  if (event && event.altKey) {
    return
  }

  if (event && isDefaultPrevented(event)) {
    return
  }

  // Make sure we have a string here, sometimes we may get a SafeString for example
  const url = String(_url || '')
  const isAbsoluteUrl = isHttpUrl(url)
  const isUnsupportedUrl = url && sanitizeUrl(url) !== url.trim()
  // all urls with schema (javascript:, data:), but not http:
  const isSpecialUrl = !isAbsoluteUrl && hasProperScheme(url)
  const isExternalUrl = isAbsoluteUrl && !isBaseHost(url)
  const isHashUrl = url.startsWith('#')
  const forceRefereshScheduled = isRefreshOnNavigationScheduled()

  const target =
    event && (event.metaKey || event.ctrlKey) ? '_blank' : _target || '_self'

  const element = event && event.currentTarget

  // Only <A> elements with equal href that are mouse-clicked will navigate on browsers
  const safeForBrowserHandling =
    element instanceof HTMLAnchorElement &&
    event &&
    event.type === 'click' &&
    element.getAttribute('href') === url

  if (isSpecialUrl || isUnsupportedUrl) {
    if (event && (isUnsupportedUrl || target !== '_self')) {
      event.preventDefault()
    }
    // Let browser handle it
    return
  }

  if (
    target === '_self' &&
    !isAbsoluteUrl &&
    !isHashUrl &&
    !forceRefereshScheduled
  ) {
    // navigate internally
    if (event) event.preventDefault()
    navigateTo(url)
  } else if (!safeForBrowserHandling || isExternalUrl) {
    // navigate using window.open for non-anchors and external links
    if (event) event.preventDefault()
    await onBeforeRefresh()
    openTab(url, target)
  } else if (forceRefereshScheduled && !isHashUrl) {
    if (event) event.preventDefault()
    refreshTo(url)
  }
  // Otherwise, let browser handle the click
}

function getNavigationType(): NavigationType {
  const navigationEntries = performance.getEntriesByType(
    'navigation'
  ) as PerformanceNavigationTiming[]

  if (navigationEntries.length === 0 && performance.navigation) {
    // PerformanceNavigationTiming not supported (Safari)
    // switchng to deprecated performance.navigation
    switch (performance.navigation.type) {
      case 1:
        return 'reload'
      case 2:
        return 'back_forward'
      default:
        return 'navigate'
    }
  }
  return navigationEntries[0].type
}

export function isNavigationReload() {
  return getNavigationType() === 'reload'
}

export function showNotFoundPage() {
  getStore().dispatch(showNotFoundPageAction())
}

export function showErrorPageWithoutCapture() {
  getStore().dispatch(showErrorPageAction())
}

/** Captures the error and displays a fatal error page.
 * The category, error & meta are used for capture only, the error page looks always the same. */
export function captureErrorAndShowErrorPage(
  category: string,
  error: string | Error | any,
  meta?: any | null
): void {
  captureError(category, error, {
    notified: 'errorPage',
    affects: 'page',
    ...meta,
  })

  showErrorPageWithoutCapture()
}

// @ts-ignore
window.baseNavigation = {
  history,
  navigateTo,
  replaceLocation,
  refresh,
  scheduleForceRefresh: scheduleRefreshOnNextNavigation,
}
